/**
 * Dev-only sample inbox items, for designing the page against a full list
 * before the real sources produce volume. Appended after real items in dev
 * builds only — a compliance product must never show invented regulatory
 * findings to a tenant, so production builds never import a single row from
 * here (the callsite is guarded by `import.meta.env.DEV`).
 *
 * Every id carries the `sample-` prefix so fixtures can be toggled independently.
 * The local design preview omits per-row sample badges.
 *
 * @module components/inbox/inboxSampleItems
 */
import type { InboxItem } from "../../state/inbox";

const HOUR = 60 * 60 * 1000;
const DAY = 24 * HOUR;

const ago = (milliseconds: number): string => new Date(Date.now() - milliseconds).toISOString();
const fromNow = (milliseconds: number): string => new Date(Date.now() + milliseconds).toISOString();

export const isSampleInboxItem = (item: InboxItem): boolean => item.id.startsWith("sample-");

export const SAMPLE_INBOX_ITEMS: ReadonlyArray<InboxItem> = [
  {
    kind: "policy-review",
    id: "sample-jfsc-aml",
    createdAt: ago(2 * HOUR),
    regulationTitle: "JFSC AML/CFT Handbook — section 4 amendments",
    jurisdiction: "Jersey",
    severity: "high",
    summary:
      "The JFSC has amended the customer due diligence requirements in section 4 of the " +
      "AML/CFT Handbook. Enhanced measures now apply to trust structures with nominee " +
      "shareholders, and the periodic-review clock for high-risk relationships shortens " +
      "from 12 months to 6. Three of your policies cite the superseded wording.",
    affectedPolicies: ["AML Policy", "Client Onboarding Procedure", "PEP Screening Standard"],
  },
  {
    kind: "invitation",
    id: "sample-invitation",
    createdAt: ago(5 * HOUR),
    organizationName: "Hawksford",
    organizationSlug: "hawksford",
    invitedBy: "Priya Raman",
    role: "member",
    expiresAt: fromNow(6 * DAY),
  },
  {
    kind: "policy-review",
    id: "sample-gfsc-guidance",
    createdAt: ago(DAY),
    regulationTitle: "GFSC Handbook — chapter 8 guidance update",
    jurisdiction: "Guernsey",
    severity: "medium",
    summary:
      "The GFSC has issued updated guidance on ongoing monitoring under chapter 8. The " +
      "changes clarify expectations for transaction-monitoring calibration and add worked " +
      "examples for fund administrators. One policy references the previous guidance note.",
    affectedPolicies: ["Transaction Monitoring Procedure"],
  },
  {
    kind: "policy-review",
    id: "sample-iomfsa-consult",
    createdAt: ago(3 * DAY),
    regulationTitle: "IOMFSA consultation — beneficial ownership thresholds",
    jurisdiction: "Isle of Man",
    severity: "low",
    summary:
      "The IOMFSA has opened a consultation proposing to lower the beneficial-ownership " +
      "disclosure threshold from 25% to 10%. No action is required until the consultation " +
      "closes, but two policies would need threshold updates if adopted as drafted.",
    affectedPolicies: ["Beneficial Ownership Policy", "CDD Standard"],
  },

  {
    kind: "policy-review",
    id: "sample-jfsc-sanctions",
    createdAt: ago(7 * HOUR),
    regulationTitle: "JFSC Sanctions guidance \u2014 screening procedures",
    jurisdiction: "Jersey",
    severity: "high",
    summary: "A sample sanctions-screening update with two linked procedures awaiting review.",
    affectedPolicies: ["Sanctions Policy", "Customer Screening Procedure"],
  },
  {
    kind: "policy-review",
    id: "sample-fca-resilience",
    createdAt: ago(10 * HOUR),
    regulationTitle: "FCA Operational resilience \u2014 impact tolerances",
    jurisdiction: "United Kingdom",
    severity: "medium",
    summary: "A sample review of impact-tolerance documentation and recovery testing evidence.",
    affectedPolicies: ["Operational Resilience Policy", "Business Continuity Plan"],
  },
  {
    kind: "policy-review",
    id: "sample-gfsc-outsourcing",
    createdAt: ago(15 * HOUR),
    regulationTitle: "GFSC Outsourcing guidance \u2014 supplier oversight",
    jurisdiction: "Guernsey",
    severity: "medium",
    summary: "A sample supplier-oversight update covering periodic assessments and exit planning.",
    affectedPolicies: ["Outsourcing Policy", "Third-party Risk Standard"],
  },
  {
    kind: "policy-review",
    id: "sample-jfsc-records",
    createdAt: ago(21 * HOUR),
    regulationTitle: "JFSC Record keeping \u2014 evidence retention",
    jurisdiction: "Jersey",
    severity: "low",
    summary: "A sample clarification of evidence retention expectations, provided for awareness.",
    affectedPolicies: ["Records Retention Schedule"],
  },
  {
    kind: "policy-review",
    id: "sample-fca-consumer",
    createdAt: ago(29 * HOUR),
    regulationTitle: "FCA Consumer Duty \u2014 annual board assessment",
    jurisdiction: "United Kingdom",
    severity: "high",
    summary: "A sample notification linking the annual assessment to two governance documents.",
    affectedPolicies: ["Consumer Duty Policy", "Board Reporting Standard"],
  },
  {
    kind: "policy-review",
    id: "sample-iomfsa-risk",
    createdAt: ago(38 * HOUR),
    regulationTitle: "IOMFSA Risk management \u2014 assessment methodology",
    jurisdiction: "Isle of Man",
    severity: "medium",
    summary:
      "A sample methodology update with changes to risk scoring and supporting documentation.",
    affectedPolicies: ["Enterprise Risk Policy", "Risk Assessment Methodology"],
  },
  {
    kind: "policy-review",
    id: "sample-jfsc-training",
    createdAt: ago(52 * HOUR),
    regulationTitle: "JFSC Training guidance \u2014 annual programme",
    jurisdiction: "Jersey",
    severity: "low",
    summary: "A sample training-programme update for the next policy review cycle.",
    affectedPolicies: ["Compliance Training Policy"],
  },
  {
    kind: "policy-review",
    id: "sample-gfsc-cyber",
    createdAt: ago(94 * HOUR),
    regulationTitle: "GFSC Cyber resilience \u2014 incident response",
    jurisdiction: "Guernsey",
    severity: "high",
    summary:
      "A sample incident-response update covering escalation owners and reporting timelines.",
    affectedPolicies: ["Information Security Policy", "Incident Response Plan"],
  },
  {
    kind: "policy-review",
    id: "sample-fca-complaints",
    createdAt: ago(151 * HOUR),
    regulationTitle: "FCA Complaints handling \u2014 reporting guidance",
    jurisdiction: "United Kingdom",
    severity: "medium",
    summary: "A sample reporting-guidance change linked to the complaints handling procedure.",
    affectedPolicies: ["Complaints Handling Procedure"],
  },
  {
    kind: "invitation",
    id: "sample-invite-northstar-trust",
    createdAt: ago(18 * HOUR),
    organizationName: "Northstar Trust",
    organizationSlug: "northstar-trust",
    invitedBy: "dan@northstar-trust.example",
    role: "admin",
    expiresAt: fromNow(5 * DAY),
  },
  {
    kind: "invitation",
    id: "sample-invite-harbour-advisory",
    createdAt: ago(62 * HOUR),
    organizationName: "Harbour Advisory",
    organizationSlug: "harbour-advisory",
    role: "member",
    expiresAt: fromNow(3 * DAY),
  },
  {
    kind: "invitation",
    id: "sample-invite-cedar-funds",
    createdAt: ago(126 * HOUR),
    organizationName: "Cedar Fund Services",
    organizationSlug: "cedar-funds",
    role: "member",
    expiresAt: fromNow(1 * DAY),
  },
];
